/**
 * Utilitaires pour l'affichage des ouvertures (périodes, horaires hebdo, fermetures exceptionnelles)
 */

import type {
  PeriodeOuverture,
  FermetureExceptionnelle,
  HoraireOuverture,
  TimeFrame,
} from "../types/apidae.d";

const WEEKDAY_ORDER = [
  "MON",
  "TUE",
  "WED",
  "THU",
  "FRI",
  "SAT",
  "SUN",
] as const;

/** 2024-01-01 is Monday; indexes 0..6 = MON..SUN */
const REFERENCE_MONDAY = new Date(2024, 0, 1);

function getWeekdayNameForLocale(weekdayKey: string, locale: string): string {
  const i = WEEKDAY_ORDER.indexOf(weekdayKey as (typeof WEEKDAY_ORDER)[number]);
  if (i < 0) return weekdayKey;
  const d = new Date(REFERENCE_MONDAY);
  d.setDate(d.getDate() + i);
  const s = new Intl.DateTimeFormat(locale, { weekday: "long" }).format(d);
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** Format "10:00" -> "10h", "12:30" -> "12h30" */
export function formatTimeDisplay(time: string): string {
  if (!time) return "";
  const parts = time.trim().split(":");
  const h = parts[0] ? parseInt(parts[0], 10) : 0;
  const m = parts[1] ? parseInt(parts[1], 10) : 0;
  if (m === 0) return `${h}h`;
  return `${h}h${m.toString().padStart(2, "0")}`;
}

export interface WeeklyScheduleRow {
  dayName: string;
  text: string;
}

/**
 * Construit le tableau d'horaires par jour (Lundi .. Dimanche) à partir de periode.horaires.
 * Chaque entrée contient le libellé du jour (selon locale) et le texte (ex. "10h - 12h30 / 13h30 - 17h", "Ouvert" ou "Fermé").
 * En l'absence d'horaires (ex. OUVERTURE_TOUS_LES_JOURS sans horaires), on affiche openLabel sur tous les jours.
 * @param locale - Code locale (ex. "fr-FR") pour les noms de jours ; utilisé si getDayLabel n'est pas fourni.
 */
export function buildWeeklyScheduleFromHoraires(
  horaires: HoraireOuverture[] | undefined,
  options?: {
    locale?: string;
    getDayLabel?: (weekdayKey: string) => string;
    closedLabel?: string;
    /** Libellé affiché pour chaque jour quand il n'y a pas d'horaires (ex. période OUVERTURE_TOUS_LES_JOURS). Par défaut "Ouvert". */
    openLabel?: string;
    /** Libellé pour les jours sans horaire renseigné (au lieu de closedLabel). Par défaut non défini (on utilise closedLabel). */
    noDataLabel?: string;
  },
): WeeklyScheduleRow[] {
  const closedLabel = options?.closedLabel ?? "Fermé";
  const openLabel = options?.openLabel ?? "Ouvert";
  const noDataLabel = options?.noDataLabel;
  const dayLabel = (key: string) =>
    options?.getDayLabel?.(key) ??
    (options?.locale ? getWeekdayNameForLocale(key, options.locale) : key);

  const byDay: Record<string, string[]> = {};
  WEEKDAY_ORDER.forEach((d) => (byDay[d] = []));

  if (!horaires?.length) {
    return WEEKDAY_ORDER.map((d) => ({
      dayName: dayLabel(d),
      text: openLabel,
    }));
  }

  for (const h of horaires) {
    const periods = h.timePeriods ?? [];
    for (const period of periods) {
      const closed = period.closed;
      const weekdays = period.weekdays ?? [];
      const frames = period.timeFrames ?? [];

      const parts: string[] = [];
      if (closed) {
        parts.push(closedLabel);
      } else {
        for (const f of frames) {
          const frame = f as TimeFrame;
          if (frame.startTime != null || frame.endTime != null) {
            const start = frame.startTime
              ? formatTimeDisplay(frame.startTime)
              : "?";
            const end = frame.endTime ? formatTimeDisplay(frame.endTime) : "?";
            parts.push(`${start} - ${end}`);
          } else {
            // TimeFrame sans horaires (ex. uniquement id) → considérer comme ouvert
            parts.push(openLabel);
          }
        }
      }
      const text = parts.filter(Boolean).join(" / ") || openLabel;
      for (const w of weekdays) {
        if (byDay[w]) byDay[w].push(text);
      }
    }
  }

  return WEEKDAY_ORDER.map((d) => {
    const texts = byDay[d];
    const text =
      texts && texts.length > 0
        ? [...new Set(texts)].join(" / ")
        : noDataLabel ?? closedLabel;
    return { dayName: dayLabel(d), text };
  });
}

/**
 * Formate une date avec ordinal pour les jours 1, 2, 3 (ex. "1er janvier", "2e février").
 * Si withYear est true, ajoute l'année (ex. "1er janvier 2026").
 */
function formatDateWithOrdinal(
  dateStr: string,
  formatDate: (dateStr: string, options?: Intl.DateTimeFormatOptions) => string,
  getOrdinalDay: (day: number) => string,
  withYear?: boolean,
): string {
  const d = new Date(dateStr);
  const day = d.getDate();
  const ordinal = getOrdinalDay(day);
  const month = formatDate(dateStr, { month: "long" });
  const base = ordinal
    ? `${ordinal} ${month}`
    : formatDate(dateStr, { day: "numeric", month: "long" });
  if (withYear) {
    return `${base} ${d.getFullYear()}`;
  }
  return base;
}

/**
 * Indique si une fermeture exceptionnelle a des dates à afficher (tableau dates ou dateFermeture).
 */
export function hasFermetureDisplayDate(f: FermetureExceptionnelle): boolean {
  return !!(f.dates?.length || f.dateFermeture);
}

/**
 * Retourne la date du jour au format YYYY-MM-DD (date locale).
 */
function getTodayDateString(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/**
 * Retourne les dates effectives d'une fermeture (dates[] ou [dateFermeture] si seule dateFermeture est renseignée).
 */
function getFermetureDates(f: FermetureExceptionnelle): string[] {
  if (f.dates?.length) return f.dates;
  if (f.dateFermeture) return [f.dateFermeture];
  return [];
}

/**
 * Filtre les fermetures exceptionnelles pour une période donnée : ne garde que celles qui ont au moins une date
 * actuelle ou future ET dans la plage de la période (ou toute date si période tousLesAns).
 * Prend en compte à la fois dates[] et dateFermeture.
 */
export function filterFermeturesForPeriod(
  fermetures: FermetureExceptionnelle[],
  periode: PeriodeOuverture,
  referenceDate?: string,
): FermetureExceptionnelle[] {
  const today = referenceDate ?? getTodayDateString();
  const debut = periode.dateDebut ?? "";
  const fin = periode.dateFin ?? "9999-12-31";
  const tousLesAns = periode.tousLesAns === true;
  const result: FermetureExceptionnelle[] = [];
  for (const f of fermetures) {
    const effectiveDates = getFermetureDates(f);
    if (effectiveDates.length === 0) continue;
    const validDates = effectiveDates.filter(
      (d) => d >= today && (tousLesAns || (d >= debut && d <= fin)),
    );
    if (validDates.length === 0) continue;
    result.push({ ...f, dates: validDates });
  }
  return result;
}

export interface FormatFermeturesOptions {
  referenceDate?: string;
  /** Si fourni, pour une fermeture avec dateSpeciale on affiche ce libellé (une fois) au lieu des dates. Sinon on affiche chaque date avec l’année. */
  getDateSpecialeLabel?: (dateSpecialeKey: string) => string;
}

/**
 * Formate la liste des fermetures exceptionnelles en un seul texte.
 * Les fermetures doivent déjà être filtrées par période si besoin (voir filterFermeturesForPeriod).
 * Si getDateSpecialeLabel est fourni et qu’une fermeture a dateSpeciale, on affiche le libellé traduit une fois. Sinon on affiche chaque date avec l’année.
 * On ne garde que les dates >= referenceDate (aujourd’hui ou futures).
 */
export function formatFermeturesExceptionnelles(
  fermetures: FermetureExceptionnelle[] | undefined,
  formatDate: (dateStr: string, options?: Intl.DateTimeFormatOptions) => string,
  prefix: string,
  getOrdinalDay: (day: number) => string,
  options?: FormatFermeturesOptions,
): string | null {
  if (!fermetures?.length) return null;
  const today = options?.referenceDate ?? getTodayDateString();
  const getDateSpecialeLabel = options?.getDateSpecialeLabel;
  const parts: string[] = [];
  for (const f of fermetures) {
    const effectiveDates = f.dates?.length
      ? f.dates
      : f.dateFermeture
        ? [f.dateFermeture]
        : [];
    if (effectiveDates.length === 0) continue;
    const validDates = [...new Set(effectiveDates)]
      .filter((d) => d >= today)
      .sort();
    if (validDates.length === 0) continue;
    if (f.dateSpeciale && getDateSpecialeLabel) {
      parts.push(getDateSpecialeLabel(f.dateSpeciale));
    } else {
      for (const dateStr of validDates) {
        parts.push(
          formatDateWithOrdinal(dateStr, formatDate, getOrdinalDay, true),
        );
      }
    }
  }
  if (parts.length === 0) return null;
  const list =
    parts.length === 1
      ? parts[0]
      : parts.length === 2
        ? `${parts[0]} et ${parts[1]}`
        : `${parts.slice(0, -1).join(", ")} et ${parts[parts.length - 1]}`;
  return prefix ? `${prefix.trimEnd()} ${list}` : list;
}

/**
 * Nombre de jours d'une période (dateDebut à dateFin inclus).
 * Si dateFin absente ou égale à dateDebut, retourne 1.
 */
export function getPeriodDurationInDays(periode: PeriodeOuverture): number {
  const debut = periode.dateDebut;
  if (!debut) return 0;
  const fin = periode.dateFin ?? debut;
  const a = new Date(debut);
  const b = new Date(fin);
  const ms = b.getTime() - a.getTime();
  const days = Math.round(ms / (24 * 60 * 60 * 1000)) + 1;
  return Math.max(1, days);
}

/**
 * Filtre les périodes d'ouverture : garde les périodes "tous les ans", en cours (date de fin non échue) et futures (date de début non échue).
 */
export function filterRelevantOpeningPeriodes(
  periodes: PeriodeOuverture[],
  referenceDate?: string,
): PeriodeOuverture[] {
  const today = referenceDate ?? getTodayDateString();
  return periodes.filter((p) => {
    if (p.tousLesAns) return true;
    const debut = p.dateDebut ?? "";
    const fin = p.dateFin ?? "";
    // Période future : date de début après aujourd'hui
    if (debut > today) return true;
    // Période en cours : date de début déjà passée et date de fin non échue (ou pas de date de fin)
    if (debut <= today && (fin === "" || fin >= today)) return true;
    return false;
  });
}

/**
 * Formate "Du dd/mm/yyyy au dd/mm/yyyy" (ou "Du dd/mm au dd/mm" si tousLesAns).
 * @param labels - optionnel : periodFrom ("Du") et periodTo ("au") traduits
 * @param hideYear - si true (ex. période tous les ans), n'affiche que JJ/MM
 */
export function formatOuverturePeriodLabel(
  periode: PeriodeOuverture,
  formatDate: (dateStr: string, options?: Intl.DateTimeFormatOptions) => string,
  labels?: { periodFrom?: string; periodTo?: string },
  hideYear?: boolean,
): string | null {
  if (!periode.dateDebut) return null;
  const from = labels?.periodFrom ?? "Du";
  const to = labels?.periodTo ?? "au";
  const dateOptions: Intl.DateTimeFormatOptions = hideYear
    ? { day: "2-digit", month: "2-digit" }
    : { day: "2-digit", month: "2-digit", year: "numeric" };
  const dateDebut = formatDate(periode.dateDebut, dateOptions);
  if (periode.dateFin) {
    const dateFin = formatDate(periode.dateFin, dateOptions);
    return `${from} ${dateDebut} ${to} ${dateFin}`;
  }
  return `${from} ${dateDebut}`;
}
